import React, { useEffect, useRef, useState } from 'react';
import { apiGet } from '../../api/apiClient';
import { toastError } from '../common/toast';
import RTSpinner from '../common/RTSpinner';
import './PaymentReceiptPrint.css';

const PaymentReceiptPrint = ({ paymentId, paymentData, onClose }) => {
  const printRef = useRef();
  const [loading, setLoading] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  useEffect(() => {
    if (paymentId && !paymentData) {
      // Fetch payment receipt data if paymentId is provided
      const fetchData = async () => {
        try {
          setLoading(true);
          const data = await fetchPaymentReceiptData(paymentId);
          setReceiptData(data);
        } catch (error) {
          console.error('Error fetching payment data:', error);
          toastError('Failed to fetch payment data');
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    } else if (paymentData) {
      // Use provided paymentData
      setReceiptData(paymentData);
      // Automatically open print dialog when component mounts
      const timer = setTimeout(() => {
        handlePrint();
        if (onClose) onClose(); // Close the component after opening print
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [paymentId, paymentData]);

  // Auto-print when receiptData is available
  useEffect(() => {
    if (receiptData && !loading) {
      const timer = setTimeout(() => {
        handlePrint();
        if (onClose) onClose(); // Close the component after opening print
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [receiptData, loading]);

  const fetchPaymentReceiptData = async (paymentId) => {
    try {
      const response = await apiGet(`/payments/${paymentId}/receipt`);
      // Extract the actual data from the axios response
      return response.data || response;
    } catch (error) {
      console.error('Error fetching payment receipt data:', error);
      throw error;
    }
  };

    const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    
    const printContent = generatePrintContent();
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payment Receipt</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              font-size: 12px;
              line-height: 1.4;
              color: #333;
              background: white;
              margin: 0;
              padding: 0;
            }
            
            .receipt-container {
              max-width: 600px;
              margin: 0 auto;
              padding: 20px 30px;
              background: white;
            }
            
            .receipt-header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #153C7F;
              padding-bottom: 15px;
            }
            
            .company-name {
              font-size: 20px;
              font-weight: bold;
              color: #153C7F;
              margin-bottom: 5px;
            }
            
            .receipt-title {
              font-size: 16px;
              font-weight: 600;
              color: #666;
              margin-bottom: 10px;
            }
            
            .receipt-number, .receipt-date {
              font-size: 12px;
              color: #666;
              margin: 3px 0;
            }
            
            .receipt-details {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
              gap: 20px;
            }
            
            .customer-info, .payment-info {
              flex: 1;
              background: #f8f9fa;
              padding: 15px;
              border-radius: 8px;
              border: 1px solid #e9ecef;
            }
            
            .customer-info h3, .payment-info h3 {
              font-size: 14px;
              font-weight: 600;
              color: #153C7F;
              margin-bottom: 10px;
              border-bottom: 1px solid #e9ecef;
              padding-bottom: 5px;
            }
            
            .customer-info p, .payment-info p {
              font-size: 11px;
              margin: 5px 0;
              line-height: 1.3;
            }
            
            .invoice-table {
              margin-bottom: 20px;
            }
            
            .invoice-table h3 {
              font-size: 16px;
              font-weight: 700;
              color: #153C7F;
              margin-bottom: 10px;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 11px;
            }
            
            th, td {
              padding: 8px 6px;
              text-align: left;
              border: 1px solid #e9ecef;
            }
            
            th {
              background-color: #153C7F;
              color: white;
              font-weight: 700;
              font-size: 10px;
            }
            
            .amount-value {
              font-weight: 600;
              color: #153C7F;
            }
            
            .status-paid {
              color: #153C7F;
              font-weight: 600;
            }
            
            .status-partial {
              color: #ffc107;
              font-weight: 600;
            }
            
            .receipt-total {
              text-align: right;
              margin: 20px 0;
              padding: 15px;
              background: #f8f9fa;
              border-radius: 8px;
              border: 2px solid #153C7F;
            }
            
            .receipt-total p {
              margin-bottom: 0;
              font-size: 14px;
              font-weight: bold;
              color: #153C7F;
              padding-top: 8px;
            }
            
            .receipt-footer {
              text-align: center;
              font-size: 11px;
              color: #666;
              border-top: 1px solid #e9ecef;
              padding-top: 15px;
              margin-top: 20px;
            }
            
            .thank-you {
              font-size: 14px;
              font-weight: 600;
              color: #153C7F;
              margin-bottom: 10px;
            }
            
            @media print {
              body {
                font-size: 11px;
                margin: 0;
                padding: 0;
              }
              
              .receipt-container {
                padding: 20px 25px;
                margin: 0;
                max-width: none;
                width: auto;
              }
              
              .receipt-details {
                display: flex !important;
                justify-content: space-between !important;
                gap: 15px !important;
              }
              
              .customer-info, .payment-info {
                flex: 1 !important;
                margin-bottom: 0 !important;
              }
              
              @page {
                margin: 0.5in 0.75in;
                size: auto;
              }
            }
            }
            
            .receipt-number {
              font-size: 14px;
              color: #153C7F;
              font-weight: 600;
            }
            
            .receipt-info {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 20px;
              margin-bottom: 20px;
            }
            
            .info-section h4 {
              font-size: 13px;
              font-weight: 600;
              color: #153C7F;
              margin-bottom: 8px;
              border-bottom: 1px solid #e9ecef;
              padding-bottom: 4px;
            }
            
            .info-item {
              display: flex;
              justify-content: space-between;
              margin-bottom: 4px;
            }
            
            .info-label {
              font-weight: 500;
              color: #666;
            }
            
            .info-value {
              font-weight: 600;
              color: #333;
            }
            
            .amount-value {
              color: #153C7F;
              font-weight: bold;
            }
            
            .invoices-section {
              margin-bottom: 20px;
            }
            
            .invoices-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            
            .invoices-table th,
            .invoices-table td {
              border: 1px solid #dee2e6;
              padding: 8px;
              text-align: left;
            }
            
            .invoices-table th {
              background: #f8f9fa;
              font-weight: 600;
              color: #153C7F;
              font-size: 11px;
              text-transform: uppercase;
            }
            
            .invoices-table td {
              font-size: 11px;
            }
            
            .status-paid {
              color: #153C7F;
              font-weight: 600;
            }
            
            .status-partial {
              color: #ffc107;
              font-weight: 600;
            }
            
            .total-section {
              background: #f8f9fa;
              border: 2px solid #153C7F;
              border-radius: 8px;
              padding: 15px;
              margin-bottom: 20px;
            }
            
            .total-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 8px;
            }
            
            .total-row:last-child {
              margin-bottom: 0;
              font-size: 14px;
              font-weight: bold;
              color: #153C7F;
              border-top: 1px solid #153C7F;
              padding-top: 8px;
            }
            
            .receipt-footer {
              text-align: center;
              font-size: 11px;
              color: #666;
              border-top: 1px solid #e9ecef;
              padding-top: 15px;
            }
            
            .thank-you {
              font-size: 14px;
              font-weight: 600;
              color: #153C7F;
              margin-bottom: 10px;
            }
            
            @media print {
              body {
                font-size: 11px;
                margin: 0;
                padding: 0;
              }
              
              .receipt-container {
                padding: 20px 25px;
                margin: 0;
                max-width: none;
                width: auto;
              }
              
              @page {
                margin: 0.5in 0.75in;
                size: auto;
              }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    // Print after content loads
    printWindow.onload = () => {
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
      };
    };
  };

  const getCurrentDateTime = () => {
    return new Date().toLocaleString();
  };

  const formatPaymentMethod = (method) => {
    const methods = {
      'cash': 'Cash',
      'bank_transfer': 'Bank Transfer',
      'upi': 'UPI',
      'card': 'Card',
      'cheque': 'Cheque'
    };
    return methods[method] || method;
  };

  const generatePrintContent = () => {
    let dataToUse = receiptData || paymentData;
    
    // If no data available, show error
    if (!dataToUse) {
      return '<html><body><h1>Error: No payment data available</h1></body></html>';
    }

    // Extract payment details
    const payment = dataToUse?.payment || dataToUse;
    const pg_details = dataToUse?.pg_details;
    const invoices_updated = dataToUse?.invoices_updated || [];    return `<div class="receipt-container">
      <div class="receipt-header">
        <h1 class="company-name">RENT TICK</h1>
        <p class="receipt-title">PAYMENT RECEIPT</p>
        <p class="receipt-number">Receipt #${payment?.payment_id || 'N/A'}</p>
        <p class="receipt-date">Date: ${getCurrentDateTime()}</p>
      </div>

      <div class="receipt-details">
        <div class="customer-info">
          <h3>Customer Information</h3>
          <p><strong>Name:</strong> ${pg_details?.pg_name || 'N/A'}</p>
          <p><strong>Address:</strong> ${pg_details?.address || 'N/A'}</p>
          <p><strong>Phone:</strong> ${pg_details?.phone || 'N/A'}</p>
        </div>

        <div class="payment-info">
          <h3>Payment Details</h3>
          <p><strong>Payment ID:</strong> ${payment?.payment_id || 'N/A'}</p>
          <p><strong>Payment Date:</strong> ${payment?.payment_date ? new Date(payment.payment_date).toLocaleDateString() : 'N/A'}</p>
          <p><strong>Payment Method:</strong> ${payment?.payment_method ? formatPaymentMethod(payment.payment_method) : 'N/A'}</p>
          <p><strong>Amount Paid:</strong> <span class="amount-value">₹${payment?.amount ? payment.amount.toFixed(2) : '0.00'}</span></p>
          ${payment?.reference_number ? `<p><strong>Reference:</strong> ${payment.reference_number}</p>` : ''}
        </div>
      </div>

      <div class="invoice-table">
        <h3>Invoices Paid</h3>
        <table>
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Date</th>
              <th>Remaining</th>
              <th>Amount Paid</th>
            </tr>
          </thead>
          <tbody>
            ${invoices_updated && invoices_updated.length > 0 ? invoices_updated.map((invoice) => `
              <tr>
                <td>#${invoice.invoice_number || 'N/A'}</td>
                <td>${invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A'}</td>
                <td>₹${invoice.new_amount_remaining ? invoice.new_amount_remaining.toFixed(2) : (invoice.amount_remaining ? invoice.amount_remaining.toFixed(2) : '0.00')}</td>
                <td class="amount-value">₹${invoice.amount_paid ? invoice.amount_paid.toFixed(2) : '0.00'}</td>
              </tr>
            `).join('') : '<tr><td colspan="4">No invoices found</td></tr>'}
          </tbody>
        </table>
      </div>

      <div class="receipt-total">
        <p><strong>Total Amount Paid: ₹${payment?.amount ? payment.amount.toFixed(2) : '0.00'}</strong></p>
      </div>

      <div class="receipt-footer">
        <p class="thank-you">Thank you for your payment!</p>
        <p>This is a computer-generated receipt.</p>
        <p>For any queries, please contact the management.</p>
      </div>
    </div>`;
  };

  // Show loading spinner only while fetching data, otherwise return null
  if (loading) {
    return (
      <div className="receipt-print-overlay">
        <RTSpinner message="Loading payment receipt..." />
      </div>
    );
  }
  
  // Return null - component will auto-print and close
  return null;
};

export default PaymentReceiptPrint;
